/**
 * Forum moderation push handling: where a tap lands, and the in-app banner for
 * a push that arrives while the app is foregrounded.
 *
 * - `FORUM_REVIEW` (approver: a post awaits approval) → the moderation screen.
 * - `FORUM_APPROVED` / `FORUM_REJECTED` (author: the decision) → the post / Forum.
 *
 * Android delivers these as system notification messages, which Capacitor does
 * NOT show while the app is in the foreground — before this module an approver
 * with the app open (the usual case when testing) saw nothing at all. iOS
 * presents the APNs alert itself, so the banner is Android-only (same rule as
 * `features/sessions/utils/foreground-session-push.ts`).
 */
import {
  FORUM_HOME_PATH,
  FORUM_REVIEW_TYPE,
  forumReviewPath,
  isForumModerationType,
} from '@shared/forum/review-notification'

export type ForumPushData = Record<string, string | undefined>

/** In-app paths only — never navigate to a payload-supplied external/odd target. */
function safeInAppPath(path: string | undefined): string | null {
  const p = path?.trim()
  if (!p || !p.startsWith('/') || p.startsWith('//')) return null
  return p
}

/** Route for a tapped Forum moderation push; `null` for any other push type. */
export function resolveForumPushRoute(data: ForumPushData | null | undefined): string | null {
  const type = data?.type
  if (!isForumModerationType(type)) return null
  // Approvers always land on the approval screen (never a payload-supplied
  // path); the post id only lets it say "Already handled".
  if (type === FORUM_REVIEW_TYPE) return forumReviewPath(data?.postId)
  return safeInAppPath(data?.path) ?? FORUM_HOME_PATH
}

export type ForegroundForumBanner = {
  title: string
  body: string
  route: string
}

export function resolveForegroundForumBanner(
  notification: { title?: string; body?: string; data?: ForumPushData },
  options: { appForegrounded: boolean; platform: 'ANDROID' | 'IOS' },
): ForegroundForumBanner | null {
  if (options.platform !== 'ANDROID' || !options.appForegrounded) return null
  const route = resolveForumPushRoute(notification.data)
  if (!route) return null
  const title = (notification.title ?? notification.data?.title ?? '').trim()
  const body = (notification.body ?? notification.data?.body ?? '').trim()
  if (!title && !body) return null
  return { title: title || 'Forum', body, route }
}
